/* eslint-disable ember/classic-decorator-hooks, ember/no-controller-access-in-routes, ember/no-runloop, warp-drive/no-legacy-request-patterns */
import Route from '@ember/routing/route';

import { next } from '@ember/runloop';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import moment from 'moment';

export default class ListRoute extends Route {
  queryParams = {
    personType: {
      refreshModel: true,
      as: 'type',
    },
    fieldKeys: {
      refreshModel: false,
      as: 'fields',
    },
    enrolmentKeys: {
      refreshModel: false,
      as: 'enrolment',
    },
    extraKeys: {
      refreshModel: false,
      as: 'extra',
    },
    medicalContactFieldKeys: {
      refreshModel: false,
      as: 'medical',
    },
    medicalContactExtraKeys: {
      refreshModel: false,
      as: 'medicalExtra',
    },
    medicalConditionFieldKeys: {
      refreshModel: false,
      as: 'condition',
    },
    medicalConditionExtraKeys: {
      refreshModel: false,
      as: 'conditionExtra',
    },
    primaryContactFieldKeys: {
      refreshModel: false,
      as: 'primary',
    },
    primaryContactExtraKeys: {
      refreshModel: false,
      as: 'primaryExtra',
    },
    secondaryContactFieldKeys: {
      refreshModel: false,
      as: 'secondary',
    },
    secondaryContactExtraKeys: {
      refreshModel: false,
      as: 'secondaryExtra',
    },
    additionalContactFieldKeys: {
      refreshModel: false,
      as: 'additional',
    },
    additionalContactExtraKeys: {
      refreshModel: false,
      as: 'additionalExtra',
    },
    includeCurrent: {
      refreshModel: true,
    },
    includeNonCurrentMedical: {
      refreshModel: true,
    },
    includePre: {
      refreshModel: true,
    },
    includeFormer: {
      refreshModel: true,
    },
    dateCalculationKeys: {
      refreshModel: false,
      as: 'dateCalculation',
    },
    dateCalculationDate: {
      refreshModel: false,
      as: 'dateCalculationDate',
    },
  };

  // Services
  @service
  dates;

  @service
  router;

  @service
  session;

  @service
  store;

  model(params) {
    let include = '';
    let statusArray = [];
    let users;

    if (params.personType === PersonType.LEARNER) {
      if (!isEmpty(params.enrolmentKeys)) {
        include = 'enrolments';
      }
      if (params.includeCurrent) {
        statusArray.push(EnrolmentStatus.CURRENT);
        statusArray.push(EnrolmentStatus.PENDING);
      }
      if (params.includePre) {
        statusArray.push(EnrolmentStatus.PRE_ENROLLED);
      }
      if (params.includeFormer) {
        statusArray.push(EnrolmentStatus.FORMER);
      }
    } else if (params.personType === PersonType.STAFF) {
      users = this.store.query('user', {
        allowedTenantAccess: this.session.activeSchool,
        scopesUnion:
          'urn:linced:organisation:role:staff,urn:linced:organisation:role:admin',
      });
    }
    let statuses = JSON.stringify(statusArray);
    let promises = {
      fields: this.store.query('people-schema/field', {
        personType: params.personType,
      }),
      fieldGroups: this.store.query('people-schema/group', {
        personType: params.personType,
      }),
      people: this.store.query('person', {
        type: params.personType,
        include,
        statuses,
      }),
      users,
    };

    if (params.dateCalculationDate) {
      let fromDate = this.fromDateForDateCalculation(
        params.dateCalculationDate,
      );
      promises.dates = this.store.query('date', {
        schoolId: this.session.activeSchool,
        from: fromDate,
        to: params.dateCalculationDate,
      });
    }

    // If listing Learners, grab some extra data
    if (params.personType === PersonType.LEARNER) {
      // Grab Groups (used for filtering)
      let year = moment().year();
      promises.groups = this.store.query('group', { year });
      if (!isEmpty(params.extraKeys)) {
        if (params.extraKeys.includes('nextYearsClass')) {
          promises.nextYearsClasses = this.store.query('group', {
            groupType: GroupType.HOMEROOM,
            year: year + 1,
          });
        }
        if (params.extraKeys.includes('previousYearsClass')) {
          promises.previousYearsClasses = this.store.query('group', {
            groupType: GroupType.HOMEROOM,
            year: year - 1,
          });
        }
      }

      // If any contact fields are selected, bring in the caregiver fields for this school
      if (
        !isEmpty(params.primaryContactFieldKeys) ||
        !isEmpty(params.secondaryContactFieldKeys) ||
        !isEmpty(params.additionalContactFieldKeys)
      ) {
        promises.caregiverGroups = this.store.query('people-schema/group', {
          personType: PersonType.CAREGIVER,
        });
        promises.caregiverFields = this.store.query('people-schema/field', {
          personType: PersonType.CAREGIVER,
        });
        promises.caregivers = this.store.query('person', {
          type: PersonType.CAREGIVER,
        });
      }
      // If any contact fields are selected, bring in the medical fields for this school
      if (!isEmpty(params.medicalContactFieldKeys)) {
        promises.medicalGroups = this.store.query('people-schema/group', {
          personType: PersonType.DOCTORS_AND_SPECIALISTS,
        });
        promises.medicalFields = this.store.query('people-schema/field', {
          personType: PersonType.DOCTORS_AND_SPECIALISTS,
        });
        promises.medicalPeople = this.store.query('person', {
          type: PersonType.DOCTORS_AND_SPECIALISTS,
        });
      }
    }

    return hash(promises);
  }

  afterModel(model) {
    if (!isEmpty(model.nextYearsClasses)) {
      let nextYearsClassMap = model.nextYearsClasses.reduce(
        (nextYearsClassMap, nextYearsClass) => {
          nextYearsClass.learners.forEach((learner) => {
            if (learner.status === 1) {
              nextYearsClassMap[learner.personId] = nextYearsClass.name;
            }
          });
          return nextYearsClassMap;
        },
        {},
      );
      model.people.forEach((person) => {
        person.set('nextYearsClass', nextYearsClassMap[person.id] || '');
      });
    }
    if (!isEmpty(model.previousYearsClasses)) {
      let previousYearsClassMap = model.previousYearsClasses.reduce(
        (previousYearsClassMap, previousYearsClass) => {
          previousYearsClass.learners.forEach((learner) => {
            if (learner.status === 1) {
              previousYearsClassMap[learner.personId] = previousYearsClass.name;
            }
          });
          return previousYearsClassMap;
        },
        {},
      );
      model.people.forEach((person) => {
        person.set(
          'previousYearsClass',
          previousYearsClassMap[person.id] || '',
        );
      });
    }
  }

  fromDateForDateCalculation(dateCalculationDate) {
    if (isEmpty(dateCalculationDate)) {
      dateCalculationDate = moment().format('YYYY-MM-DD');
    }
    let threeYearsPrior = moment(dateCalculationDate).subtract(3, 'years');
    // Align the start date with the rollover for that academic year.
    return this.dates.previousRolloverDate(
      threeYearsPrior.format('YYYY-MM-DD'),
    );
  }

  init() {
    super.init(...arguments);
    this.router.on('routeWillChange', (transition) => {
      if (isEmpty(this.controller)) {
        // Not initialised - the user hasn't entered the list route yet, so
        // can't have made any selections that we need to worry about.
        return;
      }
      // Remove selections which are no longer part of our list of people
      // When routing to the list page (usually from the list-builder route)
      if (transition.to.find((route) => route.name === this.routeName)) {
        this.controller.removeSelections();
      }
    });
  }

  activate() {
    super.activate(...arguments);
    next(this, function () {
      this.controllerFor('application').set('isWideList', true);
    });
  }

  deactivate() {
    super.deactivate(...arguments);
    this.controller.reset();
    next(this, function () {
      this.controllerFor('application').set('isWideList', false);
    });
  }
}
